import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { PropertyFilterProps } from "@amzn/awsui-components-react";
import { BulkOrderSource } from "components/BulkOrdersTemplates/interfaces";
import { BulkOrdersTemplatesService } from "services/bulk-orders-templates-service";
import { usePreferences } from "hooks/usePreferences";
import { ColumnSetting } from "common/utils/config-models";
import { defaultPagingOptions } from "common/utils/table-utils";
import { convertTokensToPropertyFilter } from "common/utils/property-filter-helper";
import tableConfig from "components/BulkOrdersTemplates/bulk-orders-templates.table-config.json";

const columns: ColumnSetting[] = tableConfig.columns as ColumnSetting[];

/** Safety cap on cursor-follow iterations when loading all pages, in case the backend never terminates the nextToken chain. */
const MAX_FETCH_PAGES = 200;
const FETCH_PAGE_LIMIT = 100;

export interface UseBulkOrdersTemplatesProps {
    preferencesKey: string;
    vendor?: string;
    status?: boolean;
    defaultPageSize?: number;
    onError?: (error: unknown) => void;
}

export interface UseBulkOrdersTemplatesResult {
    /** Every template matching the current vendor/status/filter, loaded across all cursor pages. Callers own pagination over this (e.g. via useSelectedOnlyView's fallbackItems). */
    allItems: BulkOrderSource[];
    loading: boolean;
    filterQuery: PropertyFilterProps.Query;
    setFilterQuery: (query: PropertyFilterProps.Query) => void;
    refresh: () => void;
    currentPreferences: ReturnType<typeof usePreferences>["currentPreferences"];
    onPreferencesConfirmHandler: ReturnType<typeof usePreferences>["onPreferencesConfirmHandler"];
    columns: ColumnSetting[];
}

export function useBulkOrdersTemplates({
    preferencesKey,
    vendor,
    status,
    defaultPageSize,
    onError,
}: UseBulkOrdersTemplatesProps): UseBulkOrdersTemplatesResult {
    const [allItems, setAllItems] = useState<BulkOrderSource[]>([]);
    const [loading, setLoading] = useState(true);
    const [filterQuery, setFilterQuery] = useState<PropertyFilterProps.Query>({ tokens: [], operation: "and" });
    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;

    const { currentPreferences, onPreferencesConfirmHandler } = usePreferences({
        preferencesKey,
        refreshData: () => {},
        columnsSettings: columns,
        defaultPaging: defaultPageSize || defaultPagingOptions[0].value,
    });

    const fetchIdRef = useRef(0);

    const keyedFilters = useMemo(() => convertTokensToPropertyFilter(filterQuery.tokens), [filterQuery]);

    /** Loads every page for the current vendor/status/filter by following nextToken to completion, so the full result set (and a real total) is available client-side for pagination and the "show selected only" view. */
    const doFetch = useCallback(() => {
        const id = ++fetchIdRef.current;
        setLoading(true);

        const loadAll = async () => {
            const collected: BulkOrderSource[] = [];
            let nextToken: string | undefined;
            for (let page = 0; page < MAX_FETCH_PAGES; page++) {
                const data = await BulkOrdersTemplatesService.instance.getSources({
                    vendor: vendor || undefined,
                    status,
                    limit: FETCH_PAGE_LIMIT,
                    nextToken,
                    filters: keyedFilters,
                });
                collected.push(...data.items);
                if (!data.nextToken) break;
                nextToken = data.nextToken;
            }
            return collected;
        };

        loadAll()
            .then((items) => {
                if (fetchIdRef.current === id) {
                    setAllItems(items);
                }
            })
            .catch((error) => {
                if (fetchIdRef.current === id) {
                    setAllItems([]);
                    onErrorRef.current?.(error);
                }
            })
            .finally(() => {
                if (fetchIdRef.current === id) {
                    setLoading(false);
                }
            });
    }, [vendor, status, keyedFilters]);

    useEffect(() => {
        doFetch();
    }, [doFetch]);

    return {
        allItems,
        loading,
        filterQuery,
        setFilterQuery,
        refresh: doFetch,
        currentPreferences,
        onPreferencesConfirmHandler,
        columns,
    };
}
